import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";
import { useFormik } from "formik";
import CloseIcon from "@mui/icons-material/Close";
import {
  useCreateEmailTemplatesMutation,
  useGetEmailTemplatesQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useStyles } from "../../../theme/appStyles";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import {
  EmailTemplateOption,
  IEmailTemplet,
} from "../../../Api/Interface/api.interface";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { widthofReactQuill } from "../../../Core/Enum/enum";
import { uploadAttachments } from "../../../Components/UploadFile/upload";
import { Delete } from "@mui/icons-material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

interface FormValues {
  id?: string;
  template_name: string;
  subject: string;
  message: string;
  attachments: string[];
}

const Index = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [newDepartment, { isLoading }] = useCreateEmailTemplatesMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const [selectedpolicyProductId, setSelectedpolicyProductId] = useState<
    string | ""
  >("");
  const [selectedIC, setSelectedIC] = useState<string | "">("");
  const [selectedOption, setSelectedOption] =
    useState<EmailTemplateOption | null>(null);

  const [attachments, setAttachments] = useState<File[]>([]);

  const [expandedTemplateId, setExpandedTemplateId] = useState<string | null>(
    null
  );

  const { data: userroleData, isLoading: userroleIsLoading } =
    useGetEmailTemplatesQuery({
      searchText: "",
    });

  const userroleList = useMemo(() => {
    return userroleData?.data as IEmailTemplet[];
  }, [userroleData?.data]);

  const userRoleOptions: EmailTemplateOption[] = useMemo(() => {
    if (!Array.isArray(userroleList)) return [];
    return userroleList.map((cust) => ({
      label: toProperCase(cust.template_name),
      value: cust.id,
      subject: cust.subject,
      message: cust.message,
      attachments: cust.attachments,
    }));
  }, [userroleList]);

  const formik = useFormik<FormValues>({
    initialValues: {
      id: "",
      template_name: "",
      subject: "",
      message: "",
      attachments: [],
    },
    enableReinitialize: true,

    onSubmit: async (values, { resetForm }) => {
      try {
        let uploadedUrls: string[] = [...(values.attachments || [])];

        for (const file of attachments) {
          try {
            const url = await uploadAttachments(file);
            if (url) {
              uploadedUrls.push(url);
            }
          } catch (error) {
            showMessage(`Failed to upload file ${file.name}: ${error}`);
          }
        }

        const temData = {
          subject: values.subject,
          message: values.message,
          template_name: values.template_name,
          attachments: uploadedUrls,
        };

        const temDataId = {
          id: values.id,
          subject: values.subject,
          message: values.message,
          template_name: values.template_name,
          attachments: uploadedUrls,
        };

        const payload = selectedOption ? temDataId : temData;

        const addCompanyResponse = await newDepartment(payload).unwrap();
        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage(addCompanyResponse.message);
          resetForm();
          setSelectedOption(null);
          setAttachments([]);
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formValid = useMemo(() => {
    return (
      formik.values.template_name &&
      formik.values.subject &&
      formik.values.message
    );
  }, [formik]);

  useEffect(() => {
    const query = window.location.search;
    const id = query.replace("?", "");

    if (id && userRoleOptions.length > 0) {
      const foundOption = userRoleOptions.find(
        (option: EmailTemplateOption) => String(option.value) === id
      );

      if (foundOption) {
        setSelectedOption(foundOption);

        // Prefill formik values
        formik.setValues({
          id: foundOption.value,
          template_name: foundOption.label,
          subject: foundOption.subject || "",
          message: foundOption.message || "",
          attachments: foundOption.attachments || [],
        });
      }
    }
  }, [userRoleOptions]);

  const handleAutocompleteChange = (
    event: React.SyntheticEvent,
    newValue: EmailTemplateOption | null
  ) => {
    setSelectedOption(newValue);

    if (newValue) {
      formik.setValues({
        id: newValue.value,
        template_name: newValue.label,
        subject: newValue.subject || "",
        message: newValue.message || "",
        attachments: newValue.attachments || [],
      });
    } else {
      formik.resetForm();
      setAttachments([]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setAttachments((prev) => [...prev, ...files]);
  };

  const handleRemoveFile = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const cancelFunction = () => {
    formik.resetForm();
    setAttachments([]);
  };

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Card className={classes.card}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={8}
                      md={6}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Email Template Setting
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      lg={4}
                      md={6}
                      sm={12}
                      xs={12}
                      sx={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <Autocomplete
                        options={userRoleOptions}
                        loading={userroleIsLoading}
                        // value={
                        //   userRoleOptions.find(
                        //     (option) =>
                        //       String(option.value) ===
                        //       selectedpolicyProductId
                        //   ) || null
                        // }
                        // getOptionLabel={(option: CustomerOptionString) =>
                        //   option.label
                        // }
                        // onChange={(
                        //   event,
                        //   newValue: CustomerOptionString | null
                        // ) => {
                        //   setSelectedIC(String(newValue?.value ?? ""));
                        // }}

                        value={selectedOption}
                        getOptionLabel={(option) => option.label}
                        onChange={handleAutocompleteChange}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            InputLabelProps={{ shrink: true }}
                            placeholder="Template Name  .."
                          />
                        )}
                        sx={{ flexGrow: 1, width: "100%" }}
                        size="small"
                        // className={classes.searchTextField}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} p={"24px"}>
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Template Name{" "}
                            <span className={classes.requiedfield}>*</span>
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="Template Name"
                            size="small"
                            {...formik.getFieldProps("template_name")}
                            className={classes.textField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Subject{" "}
                            <span className={classes.requiedfield}>*</span>
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="Subject"
                            size="small"
                            multiline
                            rows={3}
                            {...formik.getFieldProps("subject")}
                            className={classes.textField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item lg={12} md={12} sm={12} xs={12} pb={5}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Message{" "}
                            <span className={classes.requiedfield}>*</span>
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          {/* <TextField
                            placeholder="message"
                            size="small"
                            multiline
                            rows={5}
                            {...formik.getFieldProps("message")}
                            className={classes.textField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                          /> */}
                          <ReactQuill
                            value={formik.values.message}
                            onChange={(value) =>
                              formik.setFieldValue("message", value)
                            }
                            // onChange={(value) => {
                            //   // Remove empty paragraphs added by Quill
                            //   const cleanedValue = value.replace(/<p><br><\/p>/g, "");
                            //   formik.setFieldValue("message", cleanedValue);
                            // }}
                            placeholder="Type your message..."
                            theme="snow"
                            style={{ height: widthofReactQuill["400px"] }}
                            modules={{
                              toolbar: [
                                [{ font: [] }],
                                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                                ["bold", "italic", "underline", "strike"],
                                [{ color: [] }, { background: [] }],
                                [{ script: "sub" }, { script: "super" }],
                                [{ list: "ordered" }, { list: "bullet" }],
                                [{ indent: "-1" }, { indent: "+1" }],
                                [{ direction: "rtl" }],
                                [{ align: [] }],
                                ["blockquote", "code-block"],
                                ["link", "image", "video"],
                                ["clean"],
                              ],
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Typography variant="subtitle1">Attachments</Typography>
                      <input
                        // hidden
                        type="file"
                        multiple
                        accept="image/*,video/*,application/pdf"
                        onChange={handleFileChange}
                      />
                    </Grid>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid container spacing={2}>
                        <Grid item>
                          {formik.values?.attachments &&
                            formik.values.attachments.length > 0 && (
                              <>
                                <Grid container spacing={2}>
                                  {formik.values.attachments.map(
                                    (file: string, index: number) => {
                                      const isImage =
                                        /\.(jpeg|jpg|png|gif|webp)$/i.test(
                                          file
                                        );
                                      const isPdf = /\.pdf$/i.test(file);
                                      const isVideo = /\.mp4$/i.test(file);

                                      const handleDelete = () => {
                                        const updated =
                                          formik.values.attachments.filter(
                                            (_, i) => i !== index
                                          );
                                        formik.setFieldValue(
                                          "attachments",
                                          updated
                                        );
                                      };

                                      return (
                                        <Grid item key={index}>
                                          <div
                                            style={{
                                              position: "relative",
                                              border: "1px solid #ccc",
                                              borderRadius: 8,
                                              width: 200,
                                              height: 200,
                                              overflow: "hidden",
                                            }}
                                          >
                                            {/* Delete button */}
                                            <IconButton
                                              size="small"
                                              onClick={handleDelete}
                                              sx={{
                                                position: "absolute",
                                                top: 4,
                                                right: 4,
                                                zIndex: 2,
                                                backgroundColor:
                                                  "rgba(255,255,255,0.8)",
                                                "&:hover": {
                                                  backgroundColor:
                                                    "rgba(255,0,0,0.2)",
                                                },
                                              }}
                                            >
                                              <Delete fontSize="small" />
                                            </IconButton>

                                            {/* File display */}
                                            <a
                                              href={file}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{
                                                cursor: "pointer",
                                                display: "block",
                                                height: "100%",
                                              }}
                                            >
                                              {isImage && (
                                                <img
                                                  src={file}
                                                  alt={`attachment-${index}`}
                                                  style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                  }}
                                                />
                                              )}
                                              {isPdf && (
                                                <object
                                                  data={file}
                                                  type="application/pdf"
                                                  width="100%"
                                                  height="100%"
                                                ></object>
                                              )}
                                              {isVideo && (
                                                <video
                                                  src={file}
                                                  controls
                                                  style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                  }}
                                                />
                                              )}
                                              {!isImage &&
                                                !isPdf &&
                                                !isVideo && (
                                                  <Typography
                                                    color="primary"
                                                    sx={{
                                                      textAlign: "center",
                                                      mt: 8,
                                                      wordBreak: "break-all",
                                                    }}
                                                  >
                                                    {file.split("/").pop()}
                                                  </Typography>
                                                )}
                                            </a>
                                          </div>
                                        </Grid>
                                      );
                                    }
                                  )}
                                </Grid>
                              </>
                            )}
                        </Grid>
                        <Grid item>
                          <Grid container spacing={2}>
                            {attachments.map((file, index) => {
                              const fileUrl = URL.createObjectURL(file);
                              const isImage =
                                /\.(jpeg|jpg|png|gif|webp)$/i.test(file.name);
                              const isPdf = /\.pdf$/i.test(file.name);
                              const isVideo = /\.mp4$/i.test(file.name);

                              return (
                                <Grid item key={index}>
                                  <div
                                    style={{
                                      position: "relative",
                                      border: "1px solid #ccc",
                                      borderRadius: 8,
                                      padding: 4,
                                      width: 200,
                                      height: 200,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      overflow: "hidden",
                                      backgroundColor: "#fafafa",
                                    }}
                                  >
                                    {/* Delete button */}
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemoveFile(index)}
                                      sx={{
                                        position: "absolute",
                                        top: 4,
                                        right: 4,
                                        backgroundColor:
                                          "rgba(255,255,255,0.8)",
                                      }}
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>

                                    {/* File previews */}
                                    {isImage && (
                                      <img
                                        src={fileUrl}
                                        alt={file.name}
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                        }}
                                      />
                                    )}
                                    {isPdf && (
                                      <object
                                        data={fileUrl}
                                        type="application/pdf"
                                        width="100%"
                                        height="100%"
                                      >
                                        <Typography
                                          variant="body2"
                                          color="textSecondary"
                                        >
                                          PDF Preview Not Available
                                        </Typography>
                                      </object>
                                    )}
                                    {isVideo && (
                                      <video
                                        src={fileUrl}
                                        controls
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                        }}
                                      />
                                    )}
                                    {!isImage && !isPdf && !isVideo && (
                                      <Typography
                                        variant="body2"
                                        color="primary"
                                        sx={{ textAlign: "center" }}
                                      >
                                        {file.name}
                                      </Typography>
                                    )}
                                  </div>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item lg={12} md={12} sm={12} xs={12} pt={5}>
                      <Grid
                        container
                        spacing={2}
                        justifyContent={"space-between"}
                      >
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <Button
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            className={classes.addButton}
                            onClick={cancelFunction}
                          >
                            Reset
                          </Button>
                          <Box m={0.5}></Box>

                          <Button
                            variant="contained"
                            startIcon={
                              isLoading ? (
                                <CircularProgress
                                  size={18}
                                  className={classes.isLoading}
                                />
                              ) : selectedOption ? (
                                <CheckCircleOutlineIcon />
                              ) : (
                                <CheckCircleOutlineIcon />
                              )
                            }
                            className={classes.addButton}
                            onClick={() => formik.handleSubmit()}
                            disabled={!formValid || isLoading}
                          >
                            {selectedOption ? "Update" : "Create"}
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </form>
        </Grid>

        {/* <Grid item lg={12} md={12} sm={12} xs={12}>
          <Card className={classes.card} sx={{ minHeight: 400 }}>
            <Grid
              container
              justifyContent={"space-between"}
              spacing={3}
              sx={{ borderBottom: 2, padding: 2 }}
              className={classes.titleGrid}
            >
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                pt={2}
                alignContent={"center"}
              >
                <Typography className={classes.cardTitle}>Templates</Typography>
              </Grid>
            </Grid>
            <Grid container spacing={3} p={2}>
              {userroleList?.length ? (
                userroleList.map((template) => (
                  <Grid item xs={12} md={12} lg={12} key={template.id}>
                    <Card variant="outlined" className={classes.card}>
                      <CardContent
                        onClick={() => toggleExpand(template.id)}
                        sx={{ cursor: "pointer" }}
                      >
                        <Grid
                          container
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Grid item xs>
                            <Typography
                              variant="subtitle1"
                              fontWeight={600}
                              sx={{ cursor: "pointer" }}
                              onClick={() => toggleExpand(template.id)}
                            >
                              {template.template_name}
                            </Typography>
                          </Grid>
                          <Grid item>
                            <ExpandMore
                              expand={expandedTemplateId === template.id}
                              onClick={() => toggleExpand(template.id)}
                              aria-expanded={expandedTemplateId === template.id}
                            >
                              <ExpandMoreIcon />
                            </ExpandMore>
                          </Grid>
                        </Grid>

                        <Typography
                          variant="body2"
                          color="textSecondary"
                          mt={1}
                          noWrap
                        >
                          Subject: {template.subject}
                        </Typography>
                      </CardContent>

                      <Collapse
                        in={expandedTemplateId === template.id}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Divider />
                        <CardContent>
                          <Typography
                            component="div"
                            dangerouslySetInnerHTML={{
                              __html: template.message,
                            }}
                          />
                        </CardContent>
                      </Collapse>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Typography textAlign="center">
                    No templates found.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Card>
        </Grid> */}
      </Grid>
    </div>
  );
};

export default Index;
